import { AfterContentInit, Component, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { delay, finalize, map } from 'rxjs/operators';

import { ErrorHandlerService, OrganizationService } from '@app/services';
import { OrganizationGroupPermissions, OrganizationMemberPermissions, User } from '@app/models';
import { SelectOption } from '@app/shared';
import { Observable } from 'rxjs';
import { omit } from 'lodash';

export type ModalResult = boolean[];

enum FormSteps {
  invites,
  permissions,
  group
}

export interface ModalPublicProperties {
  organization: User['organization'];
}

@Component({
  selector: 'app-invite-organization-members-modal',
  templateUrl: './invite-organization-members-modal.component.html',
  styleUrls: ['./invite-organization-members-modal.component.scss'],
  standalone: false
})
export class InviteOrganizationMembersModalComponent implements AfterContentInit {
  @Input() organization: User['organization'];
  form!: UntypedFormGroup;
  isLoading = false;
  formStep = FormSteps.invites;
  childHasError: boolean = false;

  groups: SelectOption[] = [];
  groupsPermissions: Record<string, OrganizationGroupPermissions> = {};
  groupOverwritesPermissions = false;

  memberEmails: string[] = [];
  readonly FormSteps = FormSteps;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private organizationService: OrganizationService
  ) {
    this.form = this.formBuilder.group({
      member: this.formBuilder.group({
        email: null,
        group_id: null,
        permissions: this.formBuilder.group({
          create_documents: true,
          archive_documents: true,
          delete_documents: true,
          sign_documents: true,
          view_documents_gr: true,
          actions_documents_gr: false,
          actions_templates_gr: false,
          view_documents_oz: true,
          view_member_documents_oz: false,
          view_group_documents_oz: false,
          view_invoices_oz: true,
          actions_documents_oz: false,
          actions_members_oz: false,
          actions_groups_oz: false,
          actions_webhooks_oz: false,
          actions_applications_oz: false,
          change_appearances_oz: false,
          change_plan_oz: false,
          validate_biometry: true,
          view_member_folders_oz: false
          // change_whitelabel_oz: false // TO-DO: ACTIVATE WHEN WHITELABEL LAUNCHES
        } as OrganizationMemberPermissions)
      })
    });
  }

  ngAfterContentInit() {
    this.organizationGroups('').subscribe();
  }

  organizationGroups(search: string): Observable<SelectOption[]> {
    return this.organizationService.slimOrganizationGroupsWithPermissions({ page: 1, limit: 8, organizationId: this.organization.id, search }).pipe(
      delay(0),
      map(page => (page.data || []).filter(item => !!item)),
      map(organizations =>
        organizations.map(data => {
          this.groupsPermissions[data.id] = data.permissions;

          const option = { key: data.id, value: data.name };
          if (!this.groups.some(group => group.key === option.key)) {
            this.groups.push(option);
          }

          return option;
        })
      )
    );
  }

  onEmailsChange(emails: string[]) {
    this.memberEmails = emails;
    this.form.get('member.email')?.setValue(emails.join(', '));
  }

  getEmails() {
    return (this.form.get('member.email').value.match(/[^,\s]+/g) as string[]).map(email => (email || '').trim().toLowerCase()).filter(email => !!email);
  }

  onChildErrorChange(hasError: boolean) {
    this.childHasError = hasError;
  }

  setSelectedGroupPermissions() {
    const selectedGroup = this.groups?.find(group => group.key === this.form.get('member.group_id').value);
    const permissions = omit(this.groupsPermissions[selectedGroup.key], '__typename');
    const permissionsForm = this.form.get('member.permissions');

    if (['Administrador', 'Administrator'].includes(selectedGroup.value)) {
      Object.keys(permissions).forEach((key: keyof OrganizationGroupPermissions) => (permissions[key] = true));
    }

    this.groupOverwritesPermissions = permissions.overwrite_permissions;
    permissionsForm.patchValue(permissions);
    permissionsForm[permissions.overwrite_permissions ? 'disable' : 'enable']();
  }

  invite() {
    this.isLoading = true;
    this.form.markAllAsTouched();
    this.organizationService
      .createMember(this.getEmails().map(email => ({ member: { ...this.form.get('member').getRawValue(), email } })))
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: data => this.modal.close(data as ModalResult),
        error: error => {
          this.formStep = FormSteps.invites;
          setTimeout(() => this.errorHandlerService.handleValidation(this.form, error));
        }
      });
  }
}
